import {useChatsOfCampaignQuery} from "@/api/chatApi";
import useChatParams from "@/hooks/useChatParams";
import useUrlState from "@/hooks/useUrlState";
import {useEffect, useMemo} from "react";
import {useParams} from "react-router";
import {useMedia} from "react-use";
import {z} from "zod";

export const useChatMobileUI = () => {
  const isMobileView = useMedia("(max-width: 768px)");
  const [chatParams] = useChatSearchParams();
  const {influencerUserId} = useParams<{influencerUserId: string}>();

  const selectedMobileUI = useMemo<"overview" | "chat" | "newMessage" | "desktop">(() => {
    if (!isMobileView) return "desktop";
    if (chatParams.isBulk) return "chat";
    if (!Number(influencerUserId)) return "overview";
    return "chat";
  }, [chatParams, isMobileView, influencerUserId]);

  return {selectedMobileUI, isMobileView};
};

export const useIsCampaignChatClosed = () => {
  const [chatParams] = useChatSearchParams();
  const {influencerUserId, campaignId} = useChatParams();
  const chatsOfCampaignQuery = useChatsOfCampaignQuery({
    ...chatParams,
    campaignId: Number(campaignId),
  });
  const isCampaignClosed = useMemo(() => {
    if (influencerUserId) {
      return (
        chatsOfCampaignQuery.data?.results.find(chat => chat.user.id === Number(influencerUserId))?.has_completed ??
        false
      );
    }
    return false;
  }, [chatsOfCampaignQuery.data, influencerUserId]);

  useEffect(() => {
    if (
      !chatsOfCampaignQuery.data?.results.find(chat => chat.user.id === Number(influencerUserId)) &&
      chatsOfCampaignQuery.hasNextPage &&
      !chatsOfCampaignQuery.isFetching
    ) {
      chatsOfCampaignQuery.fetchNextPage();
    }
  }, [chatsOfCampaignQuery.data, chatsOfCampaignQuery.hasNextPage, chatsOfCampaignQuery.isFetching, influencerUserId]);

  return isCampaignClosed;
};

const stringToBoolean = (value: string | undefined) => {
  return value?.toLowerCase() === "true";
};

export const chatUrlSchema = z.object({
  isBulk: z.string().optional().transform(stringToBoolean),
  search: z.string().optional(),
});

export const useChatSearchParams = () => useUrlState(chatUrlSchema);

export const useFileType = (
  filename: string | null | undefined,
): {
  type: "image" | "video" | "zip" | "pdf" | "unknown";
  ext: string | null | undefined;
} => {
  if (!filename) return {type: "unknown", ext: null};
  const ext = filename.split("?")[0].split(".").pop()?.toLowerCase();
  switch (ext) {
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "png":
      return {type: "image", ext};
    case "m4v":
    case "avi":
    case "mpg":
    case "mp4":
    case "mov":
      return {type: "video", ext};
    case "zip":
      return {type: "zip", ext};
    case "pdf":
      return {type: "pdf", ext};
    default:
      return {type: "unknown", ext};
  }
};
